import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import * as pdfjsLib from "pdfjs-dist";
import FileSaver from "file-saver";
import { TranslateModule } from "@ngx-translate/core";
import { LoaderComponent } from "@components/layouts/loader/loader.component";
import { StatusMessageComponent } from "@components/layouts/status-message/status-message.component";
import { Constants } from "@config/constants";
import { Messages } from "@config/messages";
import { HttpService } from "@services/http.service";
import { ToastService } from "@services/toast.service";

@Component({
  selector: "app-pdf-self-sign-complete",
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    LoaderComponent,
    StatusMessageComponent,
  ],
  templateUrl: "./pdf-self-sign-complete.component.html",
  styleUrl: "./pdf-self-sign-complete.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class PdfSelfSignCompleteComponent implements OnInit, AfterViewInit, OnDestroy {
  private _httpService = inject(HttpService);
  private _toastr = inject(ToastService);
  private _cdr = inject(ChangeDetectorRef);

  @ViewChild("pdfViewport") pdfViewport?: ElementRef<HTMLDivElement>;
  @ViewChild("pdfPagesHost") pdfPagesHost?: ElementRef<HTMLDivElement>;

  @Input({ required: true }) signRequestId!: string;
  @Input() docName = "signed-document.pdf";
  @Input() previewFile: File | null = null;

  isError = false;
  isRendering = false;
  errorMessage = "";
  currentPage = 1;
  totalPages = 0;
  zoomPercent = 100;
  hasPdf = false;

  get showPreviewLoader(): boolean {
    return !this.isError && (!this.hasPdf || (this.isRendering && this.totalPages === 0));
  }

  private _pdfBlob: Blob | null = null;
  private _localPreviewBlob: Blob | null = null;
  private _pdfDoc: pdfjsLib.PDFDocumentProxy | null = null;
  private _renderSeq = 0;
  private _viewReady = false;
  private _pdfReady = false;
  private _renderScheduled = false;
  private _usingLocalPreview = false;
  private _signedPdfApplied = false;
  private _backgroundFetchTimer: ReturnType<typeof setTimeout> | null = null;
  private _pageRenderTasks = new Map<number, Promise<void>>();
  private _pageObserver: IntersectionObserver | null = null;
  private static readonly FETCH_MAX_ATTEMPTS = 24;
  private static readonly FETCH_RETRY_MS = 1500;
  private static readonly BACKGROUND_FETCH_ATTEMPTS = 16;
  private static readonly BACKGROUND_FETCH_MS = 2500;

  ngOnInit(): void {
    pdfjsLib.GlobalWorkerOptions.workerSrc = "/assets/pdf.worker.min.mjs";
    if (this.previewFile) {
      this._localPreviewBlob = this.previewFile;
    }
    this.loadSignedPdf();
  }

  ngAfterViewInit(): void {
    this._viewReady = true;
    void this.scheduleRender();
  }

  ngOnDestroy(): void {
    this._renderSeq++;
    if (this._backgroundFetchTimer != null) {
      clearTimeout(this._backgroundFetchTimer);
      this._backgroundFetchTimer = null;
    }
    this._pageObserver?.disconnect();
    this._pageObserver = null;
    this._pageRenderTasks.clear();
    this.releasePdfDoc();
    this._pdfBlob = null;
    this._localPreviewBlob = null;
  }

  downloadPdf(): void {
    if (!this._pdfBlob) {
      this._toastr.showWarning(Messages.MSG_ERROR_UNEXPECTED);
      return;
    }
    FileSaver.saveAs(this._pdfBlob, this.resolveFileName());
  }

  goToPreviousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  zoomOut(): void {
    this.setZoom(this.zoomPercent - 25);
  }

  zoomIn(): void {
    this.setZoom(this.zoomPercent + 25);
  }

  onPreviewScroll(): void {
    const host = this.pdfViewport?.nativeElement;
    const pagesHost = this.pdfPagesHost?.nativeElement;
    if (!host || !pagesHost || this.totalPages <= 0) return;

    const hostRect = host.getBoundingClientRect();
    const hostMid = hostRect.top + hostRect.height / 2;
    const pageElements = Array.from(pagesHost.querySelectorAll<HTMLElement>(".self-sign-complete__page"));

    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    pageElements.forEach((pageEl, index) => {
      const rect = pageEl.getBoundingClientRect();
      const pageMid = rect.top + rect.height / 2;
      const distance = Math.abs(pageMid - hostMid);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    this.currentPage = nearestIndex + 1;
  }

  private goToPage(page: number): void {
    if (this.totalPages <= 0) return;
    const nextPage = Math.max(1, Math.min(this.totalPages, page));
    this.currentPage = nextPage;
    const viewportHost = this.pdfViewport?.nativeElement;
    const seq = this._renderSeq;
    if (viewportHost) {
      void this.resolveViewportWidth(viewportHost).then((availableWidth) => {
        void this.ensurePageRendered(nextPage, availableWidth, seq);
      });
    }
    const pageEl = this.pdfPagesHost?.nativeElement.querySelector<HTMLElement>(
      `.self-sign-complete__page[data-page="${nextPage}"]`
    );
    pageEl?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  private setZoom(nextZoom: number): void {
    const clamped = Math.max(50, Math.min(200, nextZoom));
    if (clamped === this.zoomPercent) return;
    this.zoomPercent = clamped;
    void this.renderPdfPages();
  }

  private loadSignedPdf(): void {
    const signRequestId = this.signRequestId?.trim();
    if (!signRequestId) {
      this.isError = true;
      this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
      return;
    }

    this.fetchSignedPdf(signRequestId, 0);
  }

  private fetchSignedPdf(signRequestId: string, attempt: number, background = false): void {
    this._httpService
      .get(
        this._httpService.createEndpoint(
          Constants.API_URI_SIGN_DOWNLOAD_VIEW,
          undefined,
          { SignRequestId: signRequestId }
        )
      )
      .subscribe({
        next: (response: unknown) => {
          void this.handleFetchResponse(signRequestId, attempt, background, response);
        },
        error: (error) => {
          void this.handleFetchFailure(signRequestId, attempt, background, error);
        },
      });
  }

  private async handleFetchResponse(
    signRequestId: string,
    attempt: number,
    background: boolean,
    response: unknown,
  ): Promise<void> {
    const applied = await this.tryApplyDownloadResponse(response);
    if (applied) {
      this._pdfReady = true;
      await this.scheduleRender(true);
      return;
    }

    const maxAttempts = background
      ? PdfSelfSignCompleteComponent.BACKGROUND_FETCH_ATTEMPTS
      : PdfSelfSignCompleteComponent.FETCH_MAX_ATTEMPTS;
    const retryMs = background
      ? PdfSelfSignCompleteComponent.BACKGROUND_FETCH_MS
      : PdfSelfSignCompleteComponent.FETCH_RETRY_MS;

    if (attempt < maxAttempts) {
      window.setTimeout(
        () => this.fetchSignedPdf(signRequestId, attempt + 1, background),
        retryMs,
      );
      return;
    }

    if (!background && this._localPreviewBlob) {
      await this.activateLocalPreviewFallback();
      this.scheduleBackgroundSignedPdfFetch(signRequestId);
      return;
    }

    if (!this._localPreviewBlob) {
      this.isError = true;
      this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
    }
  }

  private async handleFetchFailure(
    signRequestId: string,
    attempt: number,
    background: boolean,
    error: unknown,
  ): Promise<void> {
    const maxAttempts = background
      ? PdfSelfSignCompleteComponent.BACKGROUND_FETCH_ATTEMPTS
      : PdfSelfSignCompleteComponent.FETCH_MAX_ATTEMPTS;
    const retryMs = background
      ? PdfSelfSignCompleteComponent.BACKGROUND_FETCH_MS
      : PdfSelfSignCompleteComponent.FETCH_RETRY_MS;

    if (attempt < maxAttempts) {
      window.setTimeout(
        () => this.fetchSignedPdf(signRequestId, attempt + 1, background),
        retryMs,
      );
      return;
    }

    if (!background && this._localPreviewBlob) {
      await this.activateLocalPreviewFallback();
      this.scheduleBackgroundSignedPdfFetch(signRequestId);
      return;
    }

    if (!this._localPreviewBlob) {
      this.isError = true;
      this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
      this._httpService.showHttpError(error);
    }
  }

  private scheduleBackgroundSignedPdfFetch(signRequestId: string): void {
    if (this._backgroundFetchTimer != null) return;
    this._backgroundFetchTimer = setTimeout(() => {
      this._backgroundFetchTimer = null;
      this.fetchSignedPdf(signRequestId, 0, true);
    }, PdfSelfSignCompleteComponent.BACKGROUND_FETCH_MS);
  }

  private async tryApplyDownloadResponse(response: unknown): Promise<boolean> {
    const parsed = this.parseDownloadResponse(response);
    if (!parsed) return false;

    const valid = await this.validatePdfBlob(parsed.blob);
    if (!valid) return false;

    if (parsed.docName) {
      this.docName = parsed.docName;
    }

    this._pdfBlob = parsed.blob;
    this.hasPdf = true;
    this._pdfReady = true;
    this._usingLocalPreview = false;
    this._signedPdfApplied = true;
    this.releasePdfDoc();
    this._pageRenderTasks.clear();
    return true;
  }

  private parseDownloadResponse(
    response: unknown,
  ): { blob: Blob; docName?: string } | null {
    const res = response as {
      IsSuccess?: boolean;
      FailRules?: Array<{ Msg?: string }>;
      Data?: Record<string, unknown>;
      data?: Record<string, unknown>;
    };

    if (res?.IsSuccess === false) {
      return null;
    }

    const data = res?.Data ?? res?.data;
    if (!data || typeof data !== "object") {
      return null;
    }

    const rawContent =
      data["DocContent"] ??
      data["docContent"] ??
      data["DocContentStr"] ??
      data["docContentStr"];

    if (typeof rawContent !== "string" || !rawContent.trim()) {
      return null;
    }

    const blob = this.base64ToPdfBlob(rawContent.trim());
    if (!blob) return null;

    const docNameRaw = data["DocName"] ?? data["docName"];
    const docName =
      typeof docNameRaw === "string" && docNameRaw.trim() ? docNameRaw.trim() : undefined;

    return { blob, docName };
  }

  private base64ToPdfBlob(base64: string): Blob | null {
    try {
      const normalized = base64.includes(",") ? base64.split(",")[1]! : base64;
      const byteCharacters = atob(normalized);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      return new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
    } catch {
      return null;
    }
  }

  private async validatePdfBlob(blob: Blob): Promise<boolean> {
    try {
      if (blob.size < 64) return false;
      const headerBuffer = await blob.slice(0, 5).arrayBuffer();
      const header = new Uint8Array(headerBuffer);
      const pdfMagic = String.fromCharCode(...header);
      return pdfMagic.startsWith("%PDF");
    } catch {
      return false;
    }
  }

  private async activateLocalPreviewFallback(): Promise<boolean> {
    if (!this._localPreviewBlob || this._signedPdfApplied) return false;
    this._pdfBlob = this._localPreviewBlob;
    this.hasPdf = true;
    this._pdfReady = true;
    this._usingLocalPreview = true;
    this.releasePdfDoc();
    this._pageRenderTasks.clear();
    await this.scheduleRender(true);
    return true;
  }

  private releasePdfDoc(): void {
    const doc = this._pdfDoc;
    this._pdfDoc = null;
    if (!doc) return;
    void doc.cleanup();
    void doc.loadingTask.destroy();
  }

  private async scheduleRender(force = false): Promise<void> {
    if (!this._viewReady || !this._pdfReady || !this._pdfBlob) return;
    if (this._renderScheduled && !force) return;
    this._renderScheduled = true;
    this._cdr.detectChanges();
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    await this.renderPdfPages();
    this._renderScheduled = false;
  }

  private async renderPdfPages(): Promise<void> {
    const pagesHost = this.pdfPagesHost?.nativeElement;
    const viewportHost = this.pdfViewport?.nativeElement;
    if (!pagesHost || !viewportHost || !this._pdfBlob) return;

    const availableWidth = await this.resolveViewportWidth(viewportHost);
    if (availableWidth <= 0) return;

    const seq = ++this._renderSeq;
    this.isRendering = true;

    try {
      if (!this._pdfDoc) {
        const buffer = await this._pdfBlob.arrayBuffer();
        if (seq !== this._renderSeq) return;
        this._pdfDoc = await pdfjsLib.getDocument({ data: buffer }).promise;
      }

      const pdf = this._pdfDoc;
      if (!pdf) return;

      this.totalPages = pdf.numPages;
      if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages || 1;
      }

      this._pageObserver?.disconnect();
      this._pageObserver = null;
      this._pageRenderTasks.clear();
      pagesHost.innerHTML = "";

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        const pageDiv = document.createElement("div");
        pageDiv.className = "self-sign-complete__page self-sign-complete__page--pending";
        pageDiv.dataset["page"] = String(pageNumber);
        pageDiv.style.minHeight = "320px";
        pagesHost.appendChild(pageDiv);
      }

      this._setupPageObserver(pagesHost, viewportHost, availableWidth, seq);
      const initialEnd = Math.min(pdf.numPages, 3);
      for (let pageNumber = 1; pageNumber <= initialEnd; pageNumber++) {
        if (seq !== this._renderSeq) return;
        await this.ensurePageRendered(pageNumber, availableWidth, seq);
      }
    } catch {
      if (seq !== this._renderSeq) return;
      if (!this._signedPdfApplied) {
        const restored = await this.activateLocalPreviewFallback();
        if (!restored && !this._localPreviewBlob) {
          this.isError = true;
          this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
        }
      } else {
        this.isError = true;
        this.errorMessage = Messages.MSG_ERROR_UNEXPECTED;
      }
    } finally {
      if (seq === this._renderSeq) {
        this.isRendering = false;
      }
    }
  }

  private _setupPageObserver(
    pagesHost: HTMLElement,
    viewportHost: HTMLElement,
    availableWidth: number,
    seq: number,
  ): void {
    this._pageObserver?.disconnect();
    this._pageObserver = new IntersectionObserver(
      (entries) => {
        if (seq !== this._renderSeq) return;
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const pageNumber = parseInt(
            (entry.target as HTMLElement).dataset["page"] || "0",
            10,
          );
          if (pageNumber > 0) {
            void this.ensurePageRendered(pageNumber, availableWidth, seq);
          }
        }
      },
      {
        root: viewportHost,
        rootMargin: "240px 0px",
        threshold: 0.01,
      },
    );

    pagesHost.querySelectorAll(".self-sign-complete__page").forEach((node) => {
      this._pageObserver?.observe(node);
    });
  }

  private async ensurePageRendered(
    pageNumber: number,
    availableWidth: number,
    seq: number,
  ): Promise<void> {
    const pagesHost = this.pdfPagesHost?.nativeElement;
    const pdf = this._pdfDoc;
    if (!pagesHost || !pdf || seq !== this._renderSeq) return;

    const pageDiv = pagesHost.querySelector<HTMLElement>(
      `.self-sign-complete__page[data-page="${pageNumber}"]`,
    );
    if (!pageDiv || pageDiv.dataset["rendered"] === "true") return;

    const pending = this._pageRenderTasks.get(pageNumber);
    if (pending) {
      await pending;
      return;
    }

    const task = this.renderSinglePage(pageNumber, pageDiv, availableWidth, seq);
    this._pageRenderTasks.set(pageNumber, task);
    try {
      await task;
    } finally {
      this._pageRenderTasks.delete(pageNumber);
    }
  }

  private async renderSinglePage(
    pageNumber: number,
    pageDiv: HTMLElement,
    availableWidth: number,
    seq: number,
  ): Promise<void> {
    const pdf = this._pdfDoc;
    if (!pdf || seq !== this._renderSeq) return;

    try {
      const page = await pdf.getPage(pageNumber);
      if (seq !== this._renderSeq) return;

      const baseViewport = page.getViewport({ scale: 1 });
      const scale = (availableWidth / baseViewport.width) * (this.zoomPercent / 100);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const context = canvas.getContext("2d");
      if (!context) return;

      await page.render({ canvasContext: context, viewport, canvas }).promise;
      if (seq !== this._renderSeq) return;

      pageDiv.classList.remove("self-sign-complete__page--pending");
      pageDiv.style.removeProperty("min-height");
      pageDiv.dataset["rendered"] = "true";
      pageDiv.replaceChildren(canvas);
    } catch {
      if (seq !== this._renderSeq) return;
      pageDiv.classList.add("self-sign-complete__page--error");
    }
  }

  private async resolveViewportWidth(viewportHost: HTMLElement): Promise<number> {
    for (let attempt = 0; attempt < 12; attempt++) {
      const width = viewportHost.clientWidth - 32;
      if (width > 0) {
        return Math.max(width, 240);
      }
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
    }
    return 320;
  }

  private resolveFileName(): string {
    const name = (this.docName || "signed-document").trim();
    return name.toLowerCase().endsWith(".pdf") ? name : `${name}.pdf`;
  }
}
